/**
 * COMPONENTE PRINCIPAL DE LA APLICACIÓN
 *
 * Se encarga de renderizar la estructura general de la aplicación:
 * - Manejo de temas (claro, oscuro, RTL).
 * - Control de navegación lateral (sidenav) y configurador.
 * - Ruteo de toda la app, con integración de autenticación y roles.
 * - Redirección inteligente en la ruta raíz (según estado de sesión).
 *
 * @author Diego Alejandro
 * @version 3.0
 */

import { useState, useEffect, useMemo } from "react";

// React Router
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// Contexto de autenticación
import { useAuth } from "context/authContext";

// Configuración centralizada de roles
import { getDashboardRoute } from "config/roleRoutes";

// Material UI
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Componentes de Material Dashboard 2
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Temas
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Rutas
import routes from "routes";

// Contextos internos
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Imágenes de marca
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

/**
 * Componente principal que define el layout y la lógica central de la app.
 */
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Contexto de autenticación para redirección y protección de rutas
  const { isAuthenticated, user, loading } = useAuth();

  /**
   * Configuración del cache RTL (right-to-left).
   */
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  /**
   * Abre el sidenav al pasar el mouse sobre el modo mini.
   */
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  /**
   * Cierra el sidenav cuando el mouse se retira.
   */
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  /**
   * Alterna el estado del configurador.
   */
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  /**
   * Ajusta el atributo `dir` del body cuando cambia la dirección (LTR/RTL).
   */
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  /**
   * Resetea el scroll al inicio al cambiar de ruta.
   */
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  /**
   * Generador de rutas dinámico a partir de la configuración centralizada.
   */
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });

  /**
   * COMPONENTE DE REDIRECCIÓN RAÍZ
   *
   * Lógica inteligente para la ruta `/`:
   * - Si está cargando el estado de autenticación → no redirige.
   * - Si está autenticado y tiene rol → redirige a su dashboard específico.
   * - Si no está autenticado → redirige a login.
   */
  const RootRedirect = () => {
    if (loading) return null;
    if (isAuthenticated && user?.role) {
      const dashboardRoute = getDashboardRoute(user.role);
      console.log(`Usuario autenticado. Redirigiendo a: ${dashboardRoute}`);
      return <Navigate to={dashboardRoute} replace />;
    }
    console.log("Usuario no autenticado. Redirigiendo a login.");
    return <Navigate to="/authentication/sign-in" replace />;
  };

  /**
   * Botón flotante de configuración.
   */
  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  // Renderizado con soporte RTL o LTR y modo claro/oscuro
  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="CUADRANTE H"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="CUADRANTE H"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
      </Routes>
    </ThemeProvider>
  );
}